import * as confluenceCfg from '../../../../uniform/components/pricing-calculator/src/configs/confluence.pricing-calculator-config';
import * as jiraCfg from '../../../../uniform/components/pricing-calculator/src/configs/jira.pricing-calculator-config';
import * as jpdCfg from '../../../../uniform/components/pricing-calculator/src/configs/jpd.pricing-calculator-config';
import * as jsmCfg from '../../../../uniform/components/pricing-calculator/src/configs/jsm.pricing-calculator-config';
import * as loomCfg from '../../../../uniform/components/pricing-calculator/src/configs/loom.pricing-calculator-config';
import * as productCollectionCfg from '../../../../uniform/components/pricing-calculator/src/configs/product-collection.pricing-calculator-config';
import * as serviceCollectionCfg from '../../../../uniform/components/pricing-calculator/src/configs/service-collection.pricing-calculator-config';
import * as talentCfg from '../../../../uniform/components/pricing-calculator/src/configs/talent.pricing-calculator-config';
import * as teamworkCollectionCfg from '../../../../uniform/components/pricing-calculator/src/configs/teamwork-collection.pricing-calculator-config';

import type { Edition, ProductCategory, ProductInputs, ProductKey } from './types';
import type {
	BillingCycle,
	Config,
	Sku,
} from '../../../../uniform/components/pricing-calculator/src/types';

/** The static (non-runtime) slice of the calculator's Config the POC reuses. */
type CalculatorConfig = Pick<
	Config,
	'planLimitConfigs' | 'billingCycleConfig' | 'unitMinEnterprise'
>;

export interface ProductPricingConfig extends CalculatorConfig {
	productKey: ProductKey;
	category: ProductCategory;
	/** Edition → SKU, taken directly from the product's config. */
	editionToSku: Partial<Record<Edition, Sku>>;
	/** Every SKU for this product, used when fetching CCP pricing data. */
	offeringSkus: readonly Sku[];
	defaults: ProductInputs;
}

// Preference order for the default edition: prefer a paid mid-tier, else fall
// back to whatever the product actually sells. Some products sell only one
// edition, so the final fallback is the first registered edition rather than a
// fixed "free".
const EDITION_PREFERENCE: Edition[] = ['standard', 'free', 'premium', 'enterprise'];

export const productPricingConfigs: Record<ProductKey, ProductPricingConfig> = {
	jira: defineProduct({
		productKey: 'jira',
		category: 'app',
		config: {
			planLimitConfigs: jiraCfg.jiraPlanLimitConfigs,
			billingCycleConfig: jiraCfg.jiraBillingCycleConfig,
			unitMinEnterprise: jiraCfg.JIRA_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: jiraCfg.jiraOfferingSkus,
		offeringSkus: jiraCfg.jiraOfferingSkusArray,
		defaultUnitCount: jiraCfg.JIRA_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: jiraCfg.JIRA_DEFAULT_BILLING_CYCLE,
	}),
	confluence: defineProduct({
		productKey: 'confluence',
		category: 'app',
		config: {
			planLimitConfigs: confluenceCfg.confluencePlanLimitConfigs,
			billingCycleConfig: confluenceCfg.confluenceBillingCycleConfig,
			unitMinEnterprise: confluenceCfg.CONFLUENCE_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: confluenceCfg.confluenceOfferingSkus,
		offeringSkus: confluenceCfg.confluenceOfferingSkusArray,
		defaultUnitCount: confluenceCfg.CONFLUENCE_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: confluenceCfg.CONFLUENCE_DEFAULT_BILLING_CYCLE,
	}),
	jsm: defineProduct({
		productKey: 'jsm',
		category: 'app',
		config: {
			planLimitConfigs: jsmCfg.jsmPlanLimitConfigs,
			billingCycleConfig: jsmCfg.jsmBillingCycleConfig,
			unitMinEnterprise: jsmCfg.JSM_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: jsmCfg.jsmOfferingSkus,
		offeringSkus: jsmCfg.jsmOfferingSkusArray,
		defaultUnitCount: jsmCfg.JSM_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: jsmCfg.JSM_DEFAULT_BILLING_CYCLE,
	}),
	jpd: defineProduct({
		productKey: 'jpd',
		category: 'app',
		config: {
			// jpd's plan-limit + sku exports are FF-gated functions, not the
			// static arrays every other config uses — call them here.
			planLimitConfigs: jpdCfg.jpdPlanLimitConfigs(),
			billingCycleConfig: jpdCfg.jpdBillingCycleConfig,
			unitMinEnterprise: jpdCfg.JPD_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: jpdCfg.jpdOfferingSkus,
		offeringSkus: jpdCfg.jpdOfferingSkusArray(),
		defaultUnitCount: jpdCfg.JPD_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: jpdCfg.JPD_DEFAULT_BILLING_CYCLE,
	}),
	loom: defineProduct({
		productKey: 'loom',
		category: 'app',
		config: {
			planLimitConfigs: loomCfg.loomPlanLimitConfigs,
			billingCycleConfig: loomCfg.loomBillingCycleConfig,
			unitMinEnterprise: loomCfg.LOOM_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: loomCfg.loomOfferingSkus,
		offeringSkus: loomCfg.loomOfferingSkusArray,
		defaultUnitCount: loomCfg.LOOM_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: loomCfg.LOOM_DEFAULT_BILLING_CYCLE,
	}),
	talent: defineProduct({
		productKey: 'talent',
		category: 'app',
		config: {
			planLimitConfigs: talentCfg.talentPlanLimitConfigs,
			billingCycleConfig: talentCfg.talentBillingCycleConfig,
			unitMinEnterprise: talentCfg.TALENT_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: talentCfg.talentOfferingSkus,
		offeringSkus: talentCfg.talentOfferingSkusArray,
		defaultUnitCount: talentCfg.TALENT_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: talentCfg.TALENT_DEFAULT_BILLING_CYCLE,
	}),
	teamworkCollection: defineProduct({
		productKey: 'teamworkCollection',
		category: 'collection',
		config: {
			planLimitConfigs: teamworkCollectionCfg.teamworkCollectionPlanLimitConfigs,
			billingCycleConfig: teamworkCollectionCfg.teamworkCollectionBillingCycleConfig,
			unitMinEnterprise: teamworkCollectionCfg.TEAMWORK_COLLECTION_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: teamworkCollectionCfg.teamworkCollectionOfferingSkus,
		offeringSkus: teamworkCollectionCfg.teamworkCollectionOfferingSkusArray,
		defaultUnitCount: teamworkCollectionCfg.TEAMWORK_COLLECTION_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: teamworkCollectionCfg.TEAMWORK_COLLECTION_DEFAULT_BILLING_CYCLE,
	}),
	serviceCollection: defineProduct({
		productKey: 'serviceCollection',
		category: 'collection',
		config: {
			planLimitConfigs: serviceCollectionCfg.serviceCollectionPlanLimitConfigs,
			billingCycleConfig: serviceCollectionCfg.serviceCollectionBillingCycleConfig,
			unitMinEnterprise: serviceCollectionCfg.SERVICE_COLLECTION_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: serviceCollectionCfg.serviceCollectionOfferingSkus,
		offeringSkus: serviceCollectionCfg.serviceCollectionOfferingSkusArray,
		defaultUnitCount: serviceCollectionCfg.SERVICE_COLLECTION_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: serviceCollectionCfg.SERVICE_COLLECTION_DEFAULT_BILLING_CYCLE,
	}),
	productCollection: defineProduct({
		productKey: 'productCollection',
		category: 'collection',
		config: {
			planLimitConfigs: productCollectionCfg.productCollectionPlanLimitConfigs,
			billingCycleConfig: productCollectionCfg.productCollectionBillingCycleConfig,
			unitMinEnterprise: productCollectionCfg.PRODUCT_COLLECTION_MIN_ENTERPRISE_USER_COUNT,
		},
		editionToSku: productCollectionCfg.productCollectionOfferingSkus,
		offeringSkus: productCollectionCfg.productCollectionOfferingSkusArray,
		defaultUnitCount: productCollectionCfg.PRODUCT_COLLECTION_DEFAULT_UNIT_COUNT,
		defaultBillingCycle: productCollectionCfg.PRODUCT_COLLECTION_DEFAULT_BILLING_CYCLE,
	}),
};

export const productPricingConfigKeys = Object.keys(productPricingConfigs) as ProductKey[];

export const getProductPricingConfig = (productKey: ProductKey): ProductPricingConfig => {
	const def = productPricingConfigs[productKey];
	if (!def) {
		throw new Error(`No UBP product pricing config registered for "${productKey}"`);
	}
	return def;
};

interface DefineProductArgs {
	productKey: ProductKey;
	category: ProductCategory;
	config: CalculatorConfig;
	/** The config's `<product>OfferingSkus` edition → SKU map. */
	editionToSku: Partial<Record<Edition, Sku>>;
	/** The config's `<product>OfferingSkusArray`. */
	offeringSkus: readonly Sku[];
	defaultUnitCount: number;
	defaultBillingCycle: BillingCycle;
}

function defineProduct(args: DefineProductArgs): ProductPricingConfig {
	const defaultEdition =
		EDITION_PREFERENCE.find((edition) => args.editionToSku[edition]) ??
		(Object.keys(args.editionToSku)[0] as Edition);

	return {
		productKey: args.productKey,
		category: args.category,
		planLimitConfigs: args.config.planLimitConfigs,
		billingCycleConfig: args.config.billingCycleConfig,
		unitMinEnterprise: args.config.unitMinEnterprise,
		editionToSku: args.editionToSku,
		offeringSkus: args.offeringSkus,
		defaults: {
			edition: defaultEdition,
			unitCount: args.defaultUnitCount,
			billingCycle: args.defaultBillingCycle,
		},
	};
}
